import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { parse } from 'qs';
import { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { MainTitle, Paragraph, TextField, WarningAlert } from 'common/components';
import { openInNewTab } from 'common/helpers';
import { Button, PasswordField } from 'components/common';
import { Divider } from 'components/styled/Divider';
import { Flex } from 'components/styled/Flex';
import { LoginSignupWrapper } from 'components/styled/Form.styled';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from 'consts/contactInfo';
import { INSTALLED } from 'helper/pwa';
import { ConfirmedRegistration } from 'modules/account/features/login/components/ConfirmedRegistration/ConfirmedRegistration';
import { ButtonSpacing, LoginLink } from 'modules/account/features/login/components/index.styled';
import { useMutationLogin } from 'modules/account/features/login/hooks';
import {
  IFormikProps,
  TUserConfirmRegistrationIdType,
} from 'modules/account/features/login/models';
import { useMutationResendActivationMail } from 'modules/account/features/singup/hooks';
import { useAuthState } from 'services/auth/AuthContext';

export const LoginSchema = Yup.object().shape({
  field_a: Yup.string().email('Invalid email').required('Required'),
  b_temp_pwd: Yup.string().required('Required'),
});

export const Login = () => {
  const { t } = useTranslation();
  const { colors, fontFamily } = useTheme();

  const { mutate: resendActivationMail } = useMutationResendActivationMail();

  const location = useLocation();

  let userConfirmRegistrationId: TUserConfirmRegistrationIdType;

  const [registrationId, setRegistrationId] = useState<TUserConfirmRegistrationIdType | boolean>(
    false,
  );
  const { setTokens } = useAuthState();

  const { mutate, status, error } = useMutationLogin({ setTokens });

  //Confirmation of user registrations
  if (location.search) {
    const { id } = parse(location.search, {
      ignoreQueryPrefix: true,
    });

    userConfirmRegistrationId = id as TUserConfirmRegistrationIdType;
  }

  useEffect(() => {
    if (userConfirmRegistrationId) {
      setRegistrationId(userConfirmRegistrationId);
    }
  }, [userConfirmRegistrationId]);

  const onSubmitForm = ({ values, formik }: IFormikProps) => {
    const payload = {
      username: values.field_a,
      password: values.b_temp_pwd,
    };

    mutate({ values: payload, formik });
  };

  const userName = window.localStorage.getItem('CUSTOMER_NAME');

  const isAdriagate = window.location.origin.includes('adriagate');

  return (
    <>
      <MainTitle
        fontFamily={fontFamily.semiBold}
        fontSize="2rem"
        fontWeight={600}
        margin="0 0 1.5rem 0"
        style={{ textAlign: 'center' }}
      >
        {userName ? `${t('Welcome back') + ', ' + userName}` : t('Login')}
      </MainTitle>

      <Formik
        validationSchema={LoginSchema}
        initialValues={{
          field_a: '',
          b_temp_pwd: '',
          isAccountConfirmed: true,
        }}
        onSubmit={(values, formik) => onSubmitForm({ values, formik })}
      >
        {({ errors, touched, values }) => {
          const { isAccountConfirmed } = errors || {};
          const { field_a } = values;

          const isAccountConfirmedVisible =
            (isAccountConfirmed as unknown as boolean) === false &&
            isAccountConfirmed !== undefined;

          return (
            <LoginSignupWrapper border="1px solid #dbdde0" autoComplete="off">
              <TextField
                label={t('Email')}
                name="field_a"
                isRequired
                disableAutoComplete="off"
                outsideValidationHasError={errors.field_a && touched.field_a}
              />

              <input type="password" style={{ display: 'none' }} autoComplete="off" />

              <PasswordField
                isRequired
                label="Password"
                name="b_temp_pwd"
                withForgotPassText
                error={errors.b_temp_pwd && touched.b_temp_pwd}
                autocomplete="off"
              />

              <ButtonSpacing>
                <Button
                  width="100%"
                  isFetching={status === 'loading'}
                  type="submit"
                  label="Login"
                  color="blue"
                />
              </ButtonSpacing>

              {registrationId && <ConfirmedRegistration id={registrationId as string} />}
              {isAccountConfirmedVisible && (
                <WarningAlert
                  text={
                    <Paragraph
                      fontFamily={fontFamily.medium}
                      fontSize="0.875rem"
                      fontWeight="500"
                      margin="0"
                      color={colors.black_80}
                      display="block"
                    >
                      {t('Account not verified')}
                      <Paragraph
                        fontFamily={fontFamily.medium}
                        fontSize="0.875rem"
                        fontWeight="500"
                        margin="0"
                        color={colors.primary_dark}
                        display="inline-block"
                        cursor="pointer"
                        onClick={() => resendActivationMail(field_a)}
                      >
                        {t('Send mail again')}
                      </Paragraph>
                    </Paragraph>
                  }
                  margin="0.75rem 0 1.5rem 0"
                  backgroundColor={colors.white}
                  error={!!error}
                  showCloseIcon={false}
                />
              )}

              <Divider margin="1.25rem 0" />

              <Paragraph
                fontFamily={fontFamily.regular}
                fontSize="0.75rem"
                fontWeight="400"
                letterSpacing="0.013rem"
                margin="0"
                justifyContent="center"
                color={colors.black_80}
              >
                {t('By logging into the system, you agree to')}
              </Paragraph>

              <Flex justifyContent="center">
                <LoginLink onClick={() => openInNewTab(TERMS_OF_SERVICE_URL)} to="#">
                  {t('Terms of service')}
                </LoginLink>
                <Paragraph
                  fontFamily={fontFamily.regular}
                  fontSize="0.75rem"
                  fontWeight="400"
                  letterSpacing="0.013rem"
                  margin="0 0.125rem"
                  color={colors.black_80}
                >
                  {t('and')}
                </Paragraph>

                <LoginLink onClick={() => openInNewTab(PRIVACY_POLICY_URL)} to="#">
                  {t('Privacy policy')}
                </LoginLink>
              </Flex>

              <Divider margin="1.25rem 0" />

              {!isAdriagate && !INSTALLED && (
                <Flex justifyContent="space-between">
                  <Paragraph
                    fontFamily={fontFamily.regular}
                    fontSize="0.75rem"
                    fontWeight="400"
                    letterSpacing="0.013rem"
                    margin="0"
                    justifyContent="center"
                  >
                    {t("Don't have an account yet?")}
                  </Paragraph>
                  <LoginLink to="/sign-up">{t('View packages')}</LoginLink>
                </Flex>
              )}
            </LoginSignupWrapper>
          );
        }}
      </Formik>
    </>
  );
};
